/**
 * 我的声音设置页 — 声音克隆管理
 * 对齐 features.md 10.5-10.7 + pages.md §十二
 * Pro 专属
 */

import { useState, useEffect } from 'react';
import { Mic, Play, Trash2, Plus } from 'lucide-react';
import { Button, IconButton } from '@hump/ui';
import { bffClient } from '../../lib/api/bff-client';

interface VoiceModel {
  id: string;
  name: string;
  status: string;
  language: string;
  isDefault: boolean;
  createdAt: string;
}

export function VoicePage() {
  const [models, setModels] = useState<VoiceModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRecord, setShowRecord] = useState(false);
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    loadModels();
  }, []);

  async function loadModels() {
    try {
      const data = await bffClient.voice.models() as any;
      setModels(data || []);
    } catch {
      // 未登录或非 Pro
    }
    setLoading(false);
  }

  return (
    <div className="space-y-4 max-w-md">
      {/* 说明 */}
      <div className="p-4 rounded-lg bg-camel/5 border border-camel/20">
        <div className="flex items-center gap-2 mb-2">
          <Mic className="w-4 h-4 text-camel" />
          <h3 className="text-sm font-medium text-camel-dark dark:text-camel">声音克隆</h3>
        </div>
        <p className="text-xs text-charcoal/60 dark:text-charcoal-light/60">
          录制约 3 分钟的声音样本，训练你的个人音色模型。之后可用你的声音朗读文档。
          <br />
          <span className="text-camel-dark dark:text-camel">Pro 专属 · 每月 60 分钟合成时长</span>
        </p>
      </div>

      {/* 音色列表 */}
      {loading ? (
        <p className="text-sm text-charcoal/50">加载中...</p>
      ) : models.length === 0 ? (
        <div className="text-center py-8">
          <Mic className="w-12 h-12 text-charcoal/20 mx-auto mb-3" />
          <p className="text-sm text-charcoal/50 mb-3">还没有克隆音色</p>
          <Button variant="primary" size="sm" onClick={() => setShowRecord(true)}>
            <Plus className="w-4 h-4 mr-1" />
            录制新音色
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {models.map((m) => (
            <div key={m.id} className="flex items-center justify-between p-3 rounded-lg bg-warm-white dark:bg-warm-dark">
              <div>
                <p className="text-sm font-medium text-charcoal dark:text-charcoal-light">{m.name}</p>
                <p className="text-xs text-charcoal/50">
                  {m.status === 'ready' ? '✓ 就绪' : m.status === 'training' ? '训练中...' : '失败'}
                  {m.isDefault && ' · 默认'}
                </p>
              </div>
              <div className="flex gap-1">
                {m.status === 'ready' && (
                  <IconButton size="sm" aria-label="试听">
                    <Play className="w-4 h-4" />
                  </IconButton>
                )}
                <IconButton size="sm" aria-label="删除">
                  <Trash2 className="w-4 h-4" />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 录制界面（简化） */}
      {showRecord && (
        <div className="p-4 rounded-lg bg-warm-white dark:bg-warm-dark space-y-3">
          <h4 className="text-sm font-medium text-charcoal dark:text-charcoal-light">录制声音样本</h4>
          <p className="text-xs text-charcoal/50">请朗读以下文本约 3 分钟，保持安静环境：</p>
          <p className="text-sm text-charcoal/70 dark:text-charcoal-light/70 italic p-3 rounded-md bg-warm-white dark:bg-border-warm-dark">
            "Attention is all you need. The dominant sequence transduction models are based on complex recurrent or convolutional neural networks..."
          </p>
          <div className="flex gap-2">
            <Button
              variant={recording ? 'danger' : 'primary'}
              size="sm"
              onClick={() => setRecording(!recording)}
            >
              <Mic className="w-4 h-4 mr-1" />
              {recording ? '停止录制' : '开始录制'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowRecord(false)}>
              取消
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
